import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import ClientModal from './components/ClientModal';

export interface Client {
  id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  document: string | null;
  document_type: string | null;
  segment: string | null;
  situation: string;
  website: string | null;
  notes: string | null;
  address_street: string | null;
  address_number: string | null;
  address_complement: string | null;
  address_neighborhood: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  _project_count?: number;
  _user_count?: number;
}

const SITUATION_OPTIONS = [
  { value: 'ativo', label: 'Ativo', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'inativo', label: 'Inativo', color: 'bg-slate-100 text-slate-600' },
  { value: 'prospecto', label: 'Prospecto', color: 'bg-sky-100 text-sky-700' },
  { value: 'suspenso', label: 'Suspenso', color: 'bg-orange-100 text-orange-700' },
  { value: 'encerrado', label: 'Encerrado', color: 'bg-red-100 text-red-700' },
];

export function getSituationStyle(situation: string) {
  return SITUATION_OPTIONS.find((s) => s.value === situation) || SITUATION_OPTIONS[0];
}

export default function AdminClientesPage() {
  const { profile } = useAuthContext();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSituation, setFilterSituation] = useState('');
  const [filterSegment, setFilterSegment] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<'dados' | 'whatsapp'>('dados');
  const [segments, setSegments] = useState<string[]>([]);
  const location = useLocation();

  const loadClients = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('name');

    if (!error && data) {
      // Buscar contagens de projetos e usuários
      const clientIds = data.map((c) => c.id);
      const [projectsRes, usersRes] = await Promise.all([
        supabase.from('projects').select('client_id').in('client_id', clientIds),
        supabase.from('client_users').select('client_id').in('client_id', clientIds).eq('is_active', true),
      ]);

      const projectCounts: Record<string, number> = {};
      const userCounts: Record<string, number> = {};
      projectsRes.data?.forEach((p) => {
        projectCounts[p.client_id] = (projectCounts[p.client_id] || 0) + 1;
      });
      usersRes.data?.forEach((u) => {
        userCounts[u.client_id] = (userCounts[u.client_id] || 0) + 1;
      });

      const enriched = data.map((c) => ({
        ...c,
        _project_count: projectCounts[c.id] || 0,
        _user_count: userCounts[c.id] || 0,
      }));

      setClients(enriched);
      const segs = [...new Set(data.map((c) => c.segment).filter(Boolean))] as string[];
      setSegments(segs);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (location.state?.openClientId && clients.length > 0) {
      const client = clients.find(c => c.id === location.state.openClientId);
      if (client) {
        setEditingClient(client);
        setActiveModalTab(location.state.openTab || 'whatsapp');
        setModalOpen(true);
        // Clear state
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, clients]);

  const filtered = clients.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      c.name.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.contact_name || '').toLowerCase().includes(q) ||
      (c.document || '').includes(q);
    const matchSituation = !filterSituation || c.situation === filterSituation;
    const matchSegment = !filterSegment || c.segment === filterSegment;
    return matchSearch && matchSituation && matchSegment;
  });

  const handleSave = async (data: Partial<Client>) => {
    if (editingClient) {
      const { error } = await supabase
        .from('clients')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', editingClient.id);
      if (!error) {
        if (profile) await logAudit(profile.id, 'update', 'clients', editingClient.id, undefined, { name: data.name });
        loadClients();
      }
    } else {
      const { data: inserted, error } = await supabase
        .from('clients')
        .insert({ ...data, created_by: profile?.id })
        .select()
        .maybeSingle();
      if (!error && inserted) {
        if (profile) await logAudit(profile.id, 'insert', 'clients', inserted.id, undefined, { name: data.name });
        loadClients();
      }
    }
    setModalOpen(false);
    setEditingClient(null);
  };

  const handleToggleActive = async (client: Client) => {
    await supabase
      .from('clients')
      .update({ is_active: !client.is_active, updated_at: new Date().toISOString() })
      .eq('id', client.id);
    if (profile) await logAudit(profile.id, 'update', 'clients', client.id, undefined, { is_active: !client.is_active });
    loadClients();
  };

  const openNew = () => {
    setEditingClient(null);
    setActiveModalTab('dados');
    setModalOpen(true);
  };

  const openEdit = (client: Client) => {
    setEditingClient(client);
    setActiveModalTab('dados');
    setModalOpen(true);
  };

  const openWhatsApp = (client: Client) => {
    setEditingClient(client);
    setActiveModalTab('whatsapp');
    setModalOpen(true);
  };

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-slate-800 text-2xl font-bold">Clientes</h1>
          <p className="text-slate-500 text-sm mt-1">
            {filtered.length} cliente{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Novo Cliente
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nome, e-mail, contato ou documento..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
          />
        </div>
        <select
          value={filterSituation}
          onChange={(e) => setFilterSituation(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
        >
          <option value="">Todas as situações</option>
          {SITUATION_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        {segments.length > 0 && (
          <select
            value={filterSegment}
            onChange={(e) => setFilterSegment(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
          >
            <option value="">Todos os segmentos</option>
            {segments.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <i className="ri-building-line text-4xl mb-3"></i>
          <p className="text-sm">Nenhum cliente encontrado</p>
          <button onClick={openNew} className="mt-4 text-cyan-500 text-sm hover:underline cursor-pointer">
            Cadastrar primeiro cliente
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Cliente</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Contato</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Segmento</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Situação</th>
                  <th className="text-center px-4 py-3 text-slate-500 font-medium">Projetos</th>
                  <th className="text-center px-4 py-3 text-slate-500 font-medium">Usuários</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((client) => {
                  const sit = getSituationStyle(client.situation);
                  return (
                    <tr key={client.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cyan-50 border border-cyan-100 shrink-0">
                            <i className="ri-building-line text-cyan-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-slate-800 font-medium">{client.name}</p>
                            {client.document && (
                              <p className="text-slate-400 text-xs">{client.document_type?.toUpperCase()} {client.document}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-slate-700">{client.contact_name || '—'}</p>
                        <p className="text-slate-400 text-xs">{client.email || client.phone || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-600 text-xs">{client.segment || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sit.color}`}>
                          {sit.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-slate-700 font-medium">{client._project_count}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-slate-700 font-medium">{client._user_count}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/admin/clientes/${client.id}`}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors cursor-pointer"
                            title="Ver detalhes"
                          >
                            <i className="ri-eye-line text-sm"></i>
                          </Link>
                          <button
                            onClick={() => openEdit(client)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                            title="Editar"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => handleToggleActive(client)}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                              client.is_active
                                ? 'text-slate-400 hover:text-orange-500 hover:bg-orange-50'
                                : 'text-slate-400 hover:text-emerald-500 hover:bg-emerald-50'
                            }`}
                            title={client.is_active ? 'Desativar' : 'Ativar'}
                          >
                            <i className={`text-sm ${client.is_active ? 'ri-toggle-line' : 'ri-toggle-fill'}`}></i>
                          </button>
                          <button
                            onClick={() => openWhatsApp(client)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 transition-colors cursor-pointer"
                            title="Falar no WhatsApp"
                          >
                            <i className="ri-whatsapp-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <ClientModal
          client={editingClient}
          initialTab={activeModalTab}
          onClose={() => { setModalOpen(false); setEditingClient(null); }}
          onSave={handleSave}
        />
      )}
    </AppLayout>
  );
}
